import { FunctionComponent, useEffect, useState } from 'react';
import { DropdownMultiselect, Icon, Input, Table, OptionListOption, Checkbox, Dropdown } from '@gaiads/telia-react-component-library';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash/cloneDeep';
import { InvoicingAccountOptionResponseContents, UserRole } from '@cdaas/api-clients/dist/asset-user-openapi';

import { showGrowlNotification } from '../../../store/actions';
import { UserGroup } from '../../../types';

import classes from './UserGroupsListRow.module.css';

type UserGroupsListRowProps = {
  userGroup: UserGroup;
  userGroupIndex: number;
  inheritedInvoicingAccount: string;
  organizationInvoicingAccounts: InvoicingAccountOptionResponseContents[];
  handleInputChange: (
    value: string | null,
    userGroupIndex: number,
    key: 'name' | 'default_user_roles' | 'lease_procurement_limit' | 'invoicing_account',
  ) => void;
  handleDeleteUserGroup: (userGroupIndex: number) => void;
  handleSaveUserGroup: (userGroup: UserGroup) => void;
};

const userRights = Object.values(UserRole) as UserRole[];

const UserGroupsListRow: FunctionComponent<UserGroupsListRowProps> = ({
  userGroup,
  userGroupIndex,
  inheritedInvoicingAccount,
  organizationInvoicingAccounts,
  handleInputChange,
  handleDeleteUserGroup,
  handleSaveUserGroup,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation('onboarding');
  const { t: tGlobal, i18n } = useTranslation('globalComponents');

  const [nameError, setNameError] = useState(false);
  const [groupError, setGroupError] = useState(false);
  const [userRightsOptions, setUserRightsOptions] = useState<OptionListOption[]>([]);

  useEffect(() => {
    const userGroupRightsDropdownOptions = userRights.map(ur => {
      const option = { label: tGlobal(`userRoles.${ur}`), value: ur, checked: false, disabled: false };
      if (userGroup.default_user_roles.includes(ur)) {
        option.checked = true;
      }
      return option;
    });
    setUserRightsOptions(userGroupRightsDropdownOptions);
  }, [i18n.language, tGlobal, userGroup.default_user_roles]);

  const handleSaveButton = (): void => {
    if (!userGroup.modified) return;
    if (!userGroup.name || userGroup.default_user_roles.length === 0) {
      dispatch(showGrowlNotification(t('userGroupFillInformationNotification'), 'warning'));
      return;
    }

    let userGroupToSave = userGroup;
    // Save procurement limit of 0 as "no limit"
    if (userGroupToSave.policies?.lease_procurement_limit === 0) {
      userGroupToSave = cloneDeep(userGroup);
      userGroupToSave.policies!.lease_procurement_limit = null;
    }

    handleSaveUserGroup(userGroupToSave);
  };

  const handleInputsOnChange = (
    input: 'name' | 'default_user_roles' | 'lease_procurement_limit' | 'invoicing_account',
    value: string | null,
  ): void => {
    handleInputChange(value, userGroupIndex, input);
    if (input === 'name') {
      if (value) setNameError(false);
      else setNameError(true);
    } else if (input === 'lease_procurement_limit') {
      // There are no possible validation errors for this field.
    } else {
      if (value) setGroupError(false);
      else setGroupError(true);
    }
  };

  const invoicingAccountOptions = organizationInvoicingAccounts.map(invoicingAccount => ({
    label: invoicingAccount.name,
    value: invoicingAccount.id,
  }));
  let selectedInvoicingAccountOption = invoicingAccountOptions.find(
    invoicingAccountOption => invoicingAccountOption.value === userGroup.invoicing_account?.id,
  );
  // If invoicing account used by this user group has been removed from telia finance
  // The user group should still store info about the no longer existing invoicing account
  let invoiceAccountNoLongerExists = false;
  if (userGroup.invoicing_account && !userGroup.invoicing_account.active) {
    // Show the info of the no longer existing invoicing account
    selectedInvoicingAccountOption = { label: userGroup.invoicing_account.name, value: userGroup.invoicing_account.id };
    // But also show error message that invoicing account no longer exists
    invoiceAccountNoLongerExists = true;
  }
  // In the dropdown options list also show an option for not having a dedicated invoicing account for this user group
  const noInvoicingAccountOptionId = '-1';
  const noInvoicingAccountOption = {
    label: t('userGroupsPage.selectNoInvoicingAccount', {
      inheritedInvoicingAccount: inheritedInvoicingAccount,
      interpolation: { escapeValue: false },
    }),
    value: noInvoicingAccountOptionId,
  };
  invoicingAccountOptions.unshift(noInvoicingAccountOption);

  const leaseProcurementLimit = userGroup.policies?.lease_procurement_limit;

  return (
    <Table.Row key={`table.row-${userGroupIndex}`} className={'tableRow'}>
      {/* Group name */}
      <Table.Cell>
        <Input
          showCustomLabel={true}
          label={''}
          value={userGroup.name}
          onChange={e => {
            handleInputsOnChange('name', e.target.value);
          }}
          errorText={nameError ? ' ' : ''}
        />
      </Table.Cell>

      {/* Rights dropdown */}
      <Table.Cell>
        <DropdownMultiselect
          showCustomLabel={true}
          onChange={(_event, option) => handleInputsOnChange('default_user_roles', option.value as UserRole)}
          onClear={() => {}}
          options={userRightsOptions}
          errorText={groupError ? ' ' : ''}
        />
      </Table.Cell>

      {/* Invoicing account */}
      <Table.Cell>
        <Dropdown
          data-testid={`usergroup-${userGroup.id}-invoicing-account`}
          showCustomLabel={true}
          onChange={(event, option) => {
            const invoicingAccountId = option.value === noInvoicingAccountOptionId ? null : option.value;
            handleInputsOnChange('invoicing_account', invoicingAccountId);
          }}
          selectedOption={selectedInvoicingAccountOption}
          options={invoicingAccountOptions}
          errorText={invoiceAccountNoLongerExists ? t('userGroupsPage.userGroupHasNoLongerExistingInvoicingAccountError') : ''}
        />
      </Table.Cell>

      {/* Price limit */}
      <Table.Cell>
        <Checkbox
          aria-label={t('userGroupsPage.priceLimitColumnTitle')}
          checked={leaseProcurementLimit != null}
          onChange={() => {
            handleInputsOnChange('lease_procurement_limit', leaseProcurementLimit == null ? '' : null);
          }}
        />
      </Table.Cell>

      {/* Max price/device */}
      <Table.Cell>
        <Input
          showCustomLabel={true}
          label={''}
          value={leaseProcurementLimit?.toString() ?? ''}
          disabled={leaseProcurementLimit == null}
          onChange={e => {
            handleInputsOnChange('lease_procurement_limit', e.target.value);
          }}
        />
      </Table.Cell>

      <Table.Cell className={classes['actions-cell']}>
        <Icon
          data-testid={`usergroup-${userGroup.id}-save-icon`}
          clickableAppearance={userGroup.modified}
          onClick={() => handleSaveButton()}
          color={userGroup.modified ? 'green700' : 'gray500'}
          margin={{ left: 'md' }}
          name={userGroup.modified ? 'save' : 'checkmark'}
          size={'md'}
        />
        <Icon
          clickableAppearance
          onClick={() => handleDeleteUserGroup(userGroupIndex)}
          color={'gray700'}
          margin={{ left: 'md' }}
          name={'delete'}
          size={'md'}
        />
      </Table.Cell>
    </Table.Row>
  );
};

export default UserGroupsListRow;
